import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { getConfig, getIncaricata, submitTest, CLUB_BASE } from "./clubApi";
import { Btn, Logo, Footer, Field, inputCls } from "./ui";
import { getMyToken, setMyToken } from "./device";

const Scala = ({ value, onChange, min_label, max_label }) => (
  <div>
    <div className="grid grid-cols-5 gap-2">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" data-testid={`scala-${n}`} onClick={() => onChange(n)}
          className={`h-14 rounded-2xl border font-club-display text-xl transition-colors ${value === n ? "border-[#E5B13A] bg-[#E5B13A] text-[#15120A]" : "border-[#2E2E34] bg-[#141417] text-[#F4EFE6] hover:border-[#E5B13A]/50"}`}>
          {n}
        </button>
      ))}
    </div>
    <div className="mt-2 flex justify-between text-[0.65rem] uppercase tracking-[0.15em] text-[#6F6A62]"><span>{min_label}</span><span>{max_label}</span></div>
  </div>
);

const Scelte = ({ opzioni, value, multi, max, onChange }) => {
  const sel = multi ? value || [] : value;
  const toggle = (k) => {
    if (!multi) return onChange(k);
    if (sel.includes(k)) return onChange(sel.filter((x) => x !== k));
    if (sel.length >= (max || 99)) return toast.message(`Puoi sceglierne al massimo ${max}`);
    onChange([...sel, k]);
  };
  return (
    <div className="space-y-2">
      {opzioni.map((o) => {
        const on = multi ? sel.includes(o.key) : sel === o.key;
        return (
          <button key={o.key} type="button" data-testid={`opt-${o.key}`} onClick={() => toggle(o.key)}
            className={`w-full rounded-2xl border px-4 py-3.5 text-left text-sm font-medium transition-colors ${on ? "border-[#E5B13A] bg-[#E5B13A]/12 text-[#F5D27A]" : "border-[#2E2E34] bg-[#141417] text-[#F4EFE6] hover:border-[#E5B13A]/50"}`}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
};

export default function TestFlow() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [inc, setInc] = useState(null);
  const [qs, setQs] = useState([]);
  const [step, setStep] = useState(-1); // -1 intro, 0..n-1 domande, n dati
  const [r, setR] = useState({});
  const [nome, setNome] = useState("");
  const [wa, setWa] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const already = getMyToken();

  useEffect(() => {
    if (already && sp.get("rifai") !== "1") { navigate(`${CLUB_BASE}/c/${already}?gia=1&da=${slug}`, { replace: true }); return; }
    Promise.all([getIncaricata(slug), getConfig()])
      .then(([i, c]) => { setInc(i); setQs(c.questions); })
      .catch(() => setErr("Questo link non è valido. Chiedi alla tua incaricata quello giusto."));
  }, [slug]); // eslint-disable-line

  const q = qs[step];
  const answered = q ? (q.opzionale ? true : q.tipo === "multi" ? (r[q.key] || []).length > 0 : r[q.key] !== undefined) : false;

  const send = async () => {
    if (!nome.trim()) return toast.error("Scrivi il tuo nome");
    if (wa.replace(/\D/g, "").length < 9) return toast.error("Scrivi il tuo numero WhatsApp: serve per mandarti il percorso");
    setBusy(true);
    try {
      const out = await submitTest({ slug, nome, whatsapp: wa, risposte: r });
      setMyToken(out.token);
      navigate(`${CLUB_BASE}/c/${out.token}?benvenuto=1`, { replace: true });
    } catch (e) { toast.error(e?.response?.data?.detail || "Qualcosa è andato storto"); }
    setBusy(false);
  };

  if (err) return <div className="mx-auto max-w-md px-6 pt-24 text-center text-[#9A948A]" data-testid="test-error">{err}</div>;
  if (!inc) return <div className="min-h-screen flex items-center justify-center"><div className="w-6 h-6 border-2 border-[#E5B13A] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="mx-auto max-w-md px-6 pt-8 pb-10" data-testid="test-flow">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3"><Logo size={32} /><span className="font-club-display text-lg">Diamond Club</span></div>
        {step >= 0 && step < qs.length && <span className="text-xs text-[#9A948A]" data-testid="test-progress">{step + 1} / {qs.length}</span>}
      </div>
      {step >= 0 && step <= qs.length && (
        <div className="mt-5 h-1 w-full rounded-full bg-[#232328] overflow-hidden"><motion.div className="h-full bg-[#E5B13A]" animate={{ width: `${(Math.min(step, qs.length) / qs.length) * 100}%` }} /></div>
      )}
      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.25 }} className="mt-10">
          {step === -1 && (
            <>
              <div className="flex items-center gap-3">
                {inc.picture ? <img src={inc.picture} alt="" className="h-12 w-12 rounded-full object-cover" referrerPolicy="no-referrer" /> : <div className="h-12 w-12 rounded-full bg-[#E5B13A]/20 flex items-center justify-center font-club-display text-lg text-[#E5B13A]">{(inc.name || "?").trim().charAt(0).toUpperCase()}</div>}
                <div><p className="text-xs uppercase tracking-[0.2em] text-[#9A948A]">Ti segue</p><p className="font-semibold" data-testid="test-incaricata-name">{inc.name}</p></div>
              </div>
              <h1 className="font-club-display text-4xl leading-[1.05] mt-8">Tre minuti per capire <span className="text-[#E5B13A]">da dove partire.</span></h1>
              <p className="mt-4 text-[#9A948A] leading-relaxed">Poche domande semplici su energia, sonno, digestione e sulla tua giornata. Alla fine hai il tuo punteggio e tre piccole abitudini da cui partire. Gratis, senza registrazione, e dall'altra parte c'è {inc.name.split(" ")[0]}: una persona vera, non un robot.</p>
              <Btn className="mt-10 w-full" onClick={() => setStep(0)} data-testid="test-start-btn">Inizio il test</Btn>
            </>
          )}
          {q && (
            <>
              <h2 className="font-club-display text-3xl leading-tight">{q.titolo}</h2>
              {q.sotto && <p className="mt-2 text-sm text-[#9A948A]">{q.sotto}</p>}
              <div className="mt-8">
                {q.tipo === "scala" && <Scala value={r[q.key]} onChange={(v) => setR({ ...r, [q.key]: v })} min_label={q.min_label} max_label={q.max_label} />}
                {q.tipo === "testo" && (
                  <textarea className={`${inputCls} ${q.multiriga ? "min-h-[140px]" : "min-h-[56px]"} text-sm leading-relaxed`} value={r[q.key] || ""} onChange={(e) => setR({ ...r, [q.key]: e.target.value.slice(0, 600) })} placeholder={q.placeholder || ""} rows={q.multiriga ? 5 : 2} data-testid={`testo-${q.key}`} />
                )}
                {(q.tipo === "scelta" || q.tipo === "multi") && <Scelte opzioni={q.opzioni} multi={q.tipo === "multi"} max={q.max} value={r[q.key]} onChange={(v) => setR({ ...r, [q.key]: v })} />}
              </div>
              <div className="mt-10 flex gap-3">
                <Btn variant="ghost" onClick={() => setStep(step - 1)} data-testid="test-back-btn">Indietro</Btn>
                <Btn className="flex-1" disabled={!answered} onClick={() => setStep(step + 1)} data-testid="test-next-btn">{q.opzionale && !(r[q.key] || "").trim() ? "Salta" : "Avanti"}</Btn>
              </div>
            </>
          )}
          {step === qs.length && qs.length > 0 && (
            <>
              <h2 className="font-club-display text-3xl leading-tight">Ultima cosa: come ti chiami?</h2>
              <p className="mt-2 text-sm text-[#9A948A]">Così {inc.name.split(" ")[0]} sa chi sei e può scriverti su WhatsApp per prepararti il percorso con cura.</p>
              <div className="mt-8 space-y-4">
                <Field label="Nome"><input className={inputCls} value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Il tuo nome" data-testid="test-nome-input" /></Field>
                <Field label="Il tuo WhatsApp" hint={`Serve a ${inc.name.split(" ")[0]} per mandarti il percorso e restare in contatto. Niente spam, niente gruppi.`}><input className={inputCls} value={wa} onChange={(e) => setWa(e.target.value)} placeholder="+39 333 1234567" inputMode="tel" required data-testid="test-wa-input" /></Field>
              </div>
              <div className="mt-10 flex gap-3">
                <Btn variant="ghost" onClick={() => setStep(step - 1)}>Indietro</Btn>
                <Btn className="flex-1" disabled={busy} onClick={send} data-testid="test-submit-btn">{busy ? "Calcolo…" : "Vedi il mio punteggio"}</Btn>
              </div>
            </>
          )}
        </motion.div>
      </AnimatePresence>
      <Footer />
    </div>
  );
}
